import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import strings from '../../utils/strings';
import { CText, CLoader } from '../../components/common';
import { FiChevronLeft, FiChevronRight, FiFilter } from 'react-icons/fi';
import { searchCompanies, CompanyDetails, CompanySearchResult } from '../../api/copilot';

const Market: React.FC = () => {
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);

  // Companies state (using MCP API)
  const [filterCriteria, setFilterCriteria] = useState('');
  const [searchResults, setSearchResults] = useState<CompanyDetails[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const pageSize = 20;

  // Load all companies when "All Companies" tab is selected
  const loadAllCompanies = async (page: number = 1, criteria: string = '') => {
    setSearchLoading(true);
    setSearchError(null);
    try {
      const result: CompanySearchResult = await searchCompanies(criteria, page, pageSize);
      if (result.status === 'success' && result.data) {
        setSearchResults(result.data);
        setHasNextPage(result.pagination?.has_next || false);
        setCurrentPage(page);
      } else {
        setSearchError(result.error || 'Failed to load companies');
      }
    } catch (err: any) {
      console.error('Error searching companies:', err);
      setSearchError(err.message || 'Failed to search companies');
    } finally {
      setSearchLoading(false);
    }
  };

  // Load companies on mount
  useEffect(() => {
    loadAllCompanies(1, filterCriteria);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    setCurrentPage(1);
    loadAllCompanies(1, filterCriteria);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      loadAllCompanies(currentPage - 1, filterCriteria);
    }
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      loadAllCompanies(currentPage + 1, filterCriteria);
    }
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === '-') return '-';
    if (typeof value === 'number') {
      if (value >= 10000000) return `${(value / 10000000).toFixed(2)} Cr`;
      if (value >= 100000) return `${(value / 100000).toFixed(2)} L`;
      return value.toLocaleString();
    }
    return String(value);
  };

  // Render All Companies tab content
  const renderAllCompaniesTab = () => (
    <div>
      {/* Filter Input */}
      <div
        className="p-4 rounded-xl mb-4"
        style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}
      >
        <div className="flex items-center gap-2 mb-2">
          {React.createElement(FiFilter as any, { size: 18, color: colors.primary })}
          <CText type="M14" color={colors.textColor}>
            Filter Companies
          </CText>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            className="flex-1 p-3 rounded-lg text-sm"
            style={{
              backgroundColor: colors.backgroundColor,
              border: `1px solid ${colors.borderColor}`,
              color: colors.textColor,
            }}
            placeholder="e.g., market_cap > 1000000 AND sector = 'Technology'"
            value={filterCriteria}
            onChange={(e) => setFilterCriteria(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button
            className="px-6 py-3 rounded-lg font-medium"
            style={{ backgroundColor: colors.primary, color: colors.white }}
            onClick={handleSearch}
            disabled={searchLoading}
          >
            {searchLoading ? 'Loading...' : 'Search'}
          </button>
        </div>
        <CText type="r12" color={colors.grayScale400} style={{ marginTop: 8 }}>
          Examples: market_cap {'>'} 1000000 | sector = 'Banking' | stock_pe {'<'} 20 | ROE {'>'} 15
        </CText>
      </div>

      {/* Loading State */}
      {searchLoading && (
        <div className="flex justify-center items-center py-12">
          <CLoader />
        </div>
      )}

      {/* Error State */}
      {searchError && !searchLoading && (
        <div className="text-center py-8">
          <CText type="M14" color={colors.alertColor}>{searchError}</CText>
          <button
            className="mt-4 px-4 py-2 rounded-lg"
            style={{ backgroundColor: colors.primary, color: colors.white }}
            onClick={() => loadAllCompanies(currentPage, filterCriteria)}
          >
            Retry
          </button>
        </div>
      )}

      {/* Company Table */}
      {!searchLoading && !searchError && searchResults.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-xl" style={{ border: `1px solid ${colors.borderColor}` }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: colors.primary }}>
                  <th className="px-4 py-3 text-left text-white font-semibold">Company</th>
                  <th className="px-4 py-3 text-left text-white font-semibold">Sector</th>
                  <th className="px-4 py-3 text-right text-white font-semibold">Market Cap</th>
                  <th className="px-4 py-3 text-right text-white font-semibold">Price</th>
                  <th className="px-4 py-3 text-right text-white font-semibold">P/E</th>
                  <th className="px-4 py-3 text-right text-white font-semibold">ROCE</th>
                  <th className="px-4 py-3 text-right text-white font-semibold">ROE</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map((company, idx) => (
                  <tr
                    key={company.name || idx}
                    className="border-b transition-colors cursor-pointer"
                    style={{
                      backgroundColor: idx % 2 === 0 ? colors.backgroundColor : colors.inputBackground,
                      borderColor: colors.borderColor,
                    }}
                    onClick={() => navigate(`/company/${encodeURIComponent(company.name)}`)}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.primary + '10')}
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        idx % 2 === 0 ? colors.backgroundColor : colors.inputBackground)
                    }
                  >
                    <td className="px-4 py-3">
                      <CText type="M14" color={colors.textColor}>
                        {company.name}
                      </CText>
                    </td>
                    <td className="px-4 py-3">
                      <CText type="r12" color={colors.grayScale500}>
                        {company.sector || '-'}
                      </CText>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <CText type="r12" color={colors.textColor}>
                        {formatValue(company.market_cap)}
                      </CText>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <CText type="r12" color={colors.textColor}>
                        {formatValue(company.current_price)}
                      </CText>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <CText type="r12" color={colors.textColor}>
                        {formatValue(company.stock_pe)}
                      </CText>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <CText type="r12" color={colors.textColor}>
                        {formatValue(company.roce)}
                      </CText>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <CText type="r12" color={colors.textColor}>
                        {formatValue(company.roe)}
                      </CText>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-center gap-4 mt-4 p-3 rounded-lg" style={{ backgroundColor: colors.inputBackground }}>
            <button
              className="flex items-center gap-1 px-4 py-2 rounded-lg font-medium transition-opacity"
              style={{
                backgroundColor: currentPage > 1 ? colors.primary : colors.grayScale400,
                color: colors.white,
                opacity: currentPage > 1 ? 1 : 0.5,
              }}
              onClick={handlePrevPage}
              disabled={currentPage <= 1 || searchLoading}
            >
              {React.createElement(FiChevronLeft as any, { size: 18 })}
              Previous
            </button>
            <CText type="M14" color={colors.textColor}>
              Page {currentPage}
            </CText>
            <button
              className="flex items-center gap-1 px-4 py-2 rounded-lg font-medium transition-opacity"
              style={{
                backgroundColor: hasNextPage ? colors.primary : colors.grayScale400,
                color: colors.white,
                opacity: hasNextPage ? 1 : 0.5,
              }}
              onClick={handleNextPage}
              disabled={!hasNextPage || searchLoading}
            >
              Next
              {React.createElement(FiChevronRight as any, { size: 18 })}
            </button>
          </div>
        </>
      )}

      {/* Empty State */}
      {!searchLoading && !searchError && searchResults.length === 0 && (
        <div className="text-center py-12">
          <CText type="M16" color={colors.grayScale500}>
            No companies found
          </CText>
          <CText type="r14" color={colors.grayScale400} style={{ marginTop: 8 }}>
            Try adjusting your filter criteria
          </CText>
        </div>
      )}
    </div>
  );

  return (
    <div
      className="p-4 lg:p-8 min-h-screen pb-24"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <div className="lg:max-w-6xl lg:mx-auto">
        {/* Header */}
        <div className="mb-4">
          <CText type="B20" color={colors.textColor}>{strings.market}</CText>
        </div>

        {/* Content */}
        {renderAllCompaniesTab()}
      </div>
    </div>
  );
};

export default Market;
